import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { RiDragMove2Line } from "react-icons/ri";
import { getAuthHeaders } from '../utils/getAuthHeaders'
import { getErrorMessage } from '../utils/getErrorMessage'
import { endpoints } from "../config/api";
import MultiSelectDropdown from "./MultiSelectDropdown";
import { buildOrgMultiSelectOptions, parseOrgIdsFromMultiSelectValues } from "../utils/orgMultiSelectOptions";
import { refreshAgentCache } from "../utils/refreshAgentCache";
import IndustryContextPicker from "./IndustryContextPicker";
import {
  applyIndustryDefaultsToMetadata,
  hasIndustryDefaults,
  isPartialIndustrySelection,
} from "../utils/industryMetadata";

// Schedule UI constants (aligned with ScheduleLibraryAgents payload)
const SCHEDULE_DAY_MAP = [
  { label: "S", name: "Sunday" },
  { label: "M", name: "Monday" },
  { label: "T", name: "Tuesday" },
  { label: "W", name: "Wednesday" },
  { label: "T", name: "Thursday" },
  { label: "F", name: "Friday" },
  { label: "S", name: "Saturday" },
];
const SCHEDULE_MAX_REPEAT = { Minute: 10080, Hour: 168, Day: 730, Week: 104, Month: 24 };
const SCHEDULE_TIMEZONES = [
  { value: "Asia/Kolkata", label: "India (IST) GMT+5:30" },
  { value: "GMT", label: "GMT GMT+0:00" },
  { value: "America/Los_Angeles", label: "USA (PST) GMT-8:00" },
  { value: "America/New_York", label: "USA (EST) GMT-5:00" },
  { value: "America/Chicago", label: "USA (CST) GMT-6:00" },
  { value: "Asia/Singapore", label: "Singapore (SGT) GMT+8:00" },
  { value: "Asia/Taipei", label: "Taiwan (CST) GMT+8:00" },
  { value: "Asia/Dubai", label: "UAE (GST) GMT+4:00" },
];

const stepId = () => `step-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const AutoResizeTextarea = ({ value, onChange, style, ...props }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.max(el.scrollHeight, 56)}px`;
  }, [value]);
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={onChange}
      style={{ ...style, resize: "none", overflow: "hidden" }}
      {...props}
    />
  );
};

const CreateAgent = ({ onAgentCreated, onClose }) => {
  const [agentData, setAgentData] = useState({
    agent_name: "",
    agent_flow_id: "chatagent",
    agent_category: "",
    description: "",
  });
  const [instructionSteps, setInstructionSteps] = useState([
    { id: stepId(), content: "" },
  ]);
  const [draggedStepIndex, setDraggedStepIndex] = useState(null);
  const [dragOverStepIndex, setDragOverStepIndex] = useState(null);
  const [lookbackPeriod, setLookbackPeriod] = useState("");
  const [lookbackUnit, setLookbackUnit] = useState("Month");
  const [webSearch, setWebSearch] = useState("Off");
  const [autoDataSelection, setAutoDataSelection] = useState(false);
  const [availableForms, setAvailableForms] = useState({ India: {}, USA: {} });
  const [selectedForms, setSelectedForms] = useState({ India: [], USA: [] });
  const [formSearch, setFormSearch] = useState({ India: "", USA: "" });
  const [webLinks, setWebLinks] = useState([]);
  const [currentLink, setCurrentLink] = useState("");
  const [writingStyle, setWritingStyle] = useState("");
  const [customWritingStyleId, setCustomWritingStyleId] = useState("");
  const [customWritingStyleName, setCustomWritingStyleName] = useState("");
  const [customWritingStyleDescription, setCustomWritingStyleDescription] = useState("");

  const [schedules, setSchedules] = useState([]);
  const [currentSchedule, setCurrentSchedule] = useState({
    date: "",
    time: "",
    timezone: "Asia/Kolkata",
    type: "once",
    repeatEvery: 1,
    repeatUnit: "Hour",
    repeatDays: { Sunday: false, Monday: false, Tuesday: false, Wednesday: false, Thursday: false, Friday: false, Saturday: false },
    endOption: "after",
    endsOn: "",
    endsAfter: 10,
    runDuringOption: "Financial Hour",
  });

  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [categories, setCategories] = useState([]);
  const [categoryIsCustom, setCategoryIsCustom] = useState(false);
  const [availableOrgs, setAvailableOrgs] = useState([]);
  const [selectedOrgIds, setSelectedOrgIds] = useState([]);
  const [industryContext, setIndustryContext] = useState({
    country: "India",
    sector: "",
    industry: "",
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(
          endpoints.agentsCategories,
          { headers: { ...getAuthHeaders() } }
        );
        setCategories(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error("Error fetching categories", err);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        const response = await axios.get(
          endpoints.orgs,
          { headers: { ...getAuthHeaders() } }
        );
        setAvailableOrgs(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error("Error fetching orgs", err);
      }
    };
    fetchOrgs();
  }, []);

  useEffect(() => {
    const fetchForms = async () => {
      try {
        const response = await axios.get(
          endpoints.agentsForms,
          {
            headers: {
              ...getAuthHeaders(),
            },
          }
        );
        setAvailableForms(response.data);
      } catch (error) {
        console.error("Error fetching form types", error);
      }
    };
    fetchForms();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAgentData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const reorderInstructionSteps = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    setInstructionSteps((prev) => {
      const next = [...prev];
      const [removed] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, removed);
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const hasInstructions = instructionSteps.some((s) => (s.content || "").trim());
    if (!hasInstructions) {
      setErrorMessage("Please add at least one instruction step.");
      return;
    }
    if (isPartialIndustrySelection(industryContext)) {
      setErrorMessage("Complete country, sector, and industry for category defaults, or clear all three.");
      return;
    }
    setLoading(true);
    setSuccessMessage("");
    setErrorMessage("");

    const instructions = instructionSteps
      .filter((s) => (s.content || "").trim())
      .map((s) => s.content.trim())
      .join(" || ");

    const baseMetadata = {
      time_period_value: lookbackPeriod,
      time_period_unit: lookbackUnit,
      internet: webSearch,
      webLinks: webLinks,
      autoAddUpcoming: autoDataSelection,
      form_types: selectedForms,
      schedules: schedules,
      writing_style: writingStyle || undefined,
      custom_writing_style_id: writingStyle === "custom" ? (customWritingStyleId || undefined) : undefined,
      custom_writing_style_name: writingStyle === "custom" ? (customWritingStyleName || undefined) : undefined,
      custom_writing_style_description: writingStyle === "custom" ? (customWritingStyleDescription || undefined) : undefined,
    };
    const metadata = applyIndustryDefaultsToMetadata(baseMetadata, industryContext);

    const payload = {
      ...agentData,
      instructions: instructions || undefined,
      organization_ids: Array.isArray(selectedOrgIds) ? selectedOrgIds.map((id) => Number(id)).filter((n) => !Number.isNaN(n)) : [],
      metadata,
    };

    try {
      const response = await axios.post(
        endpoints.agentsLibrary,
        payload,
        {
          headers: {
            ...getAuthHeaders(),
          },
        }
      );
      if (response.status === 201) {
        const createdId = response.data?.id ?? response.data?.agent_library_id;
        if (createdId && hasIndustryDefaults(industryContext)) {
          await axios.post(
            endpoints.agentsEditAgentDetails,
            { agent_library_id: createdId, metadata },
            { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } }
          );
        }
        await refreshAgentCache();
        setSuccessMessage("Agent created successfully!");
        if (onAgentCreated) {
          onAgentCreated(response.data);
        }
        setAgentData({
          agent_name: "",
          agent_flow_id: "chatagent",
          agent_category: "",
          description: "",
        });
        setInstructionSteps([{ id: stepId(), content: "" }]);
        setCategoryIsCustom(false);
        setLookbackPeriod("");
        setLookbackUnit("Month");
        setWebSearch("Off");
        setAutoDataSelection(false);
        setSelectedForms({ India: [], USA: [] });
        setSchedules([]);
        setCurrentSchedule({
          date: "",
          time: "",
          timezone: "Asia/Kolkata",
          type: "once",
          repeatEvery: 1,
          repeatUnit: "Hour",
          repeatDays: { Sunday: false, Monday: false, Tuesday: false, Wednesday: false, Thursday: false, Friday: false, Saturday: false },
          endOption: "after",
          endsOn: "",
          endsAfter: 10,
          runDuringOption: "Financial Hour",
        });
        setWritingStyle("");
        setCustomWritingStyleId("");
        setCustomWritingStyleName("");
        setCustomWritingStyleDescription("");
        setSelectedOrgIds([]);
        setIndustryContext({ country: "India", sector: "", industry: "" });
      }
    } catch (error) {
      console.error("Error creating agent", error?.response);
      setErrorMessage(getErrorMessage(error, "Failed to create agent"));
    } finally {
      setLoading(false);
    }
  };

  const handleFormSelection = (country, formKey, isChecked) => {
    setSelectedForms((prev) => {
      const currentList = prev[country] || [];
      if (isChecked) {
        return { ...prev, [country]: [...currentList, formKey] };
      } else {
        return { ...prev, [country]: currentList.filter((key) => key !== formKey) };
      }
    });
  };

  const buildScheduleFromCurrent = () => {
    const type = currentSchedule.type === "weekday" ? "weekdays" : currentSchedule.type;
    const schedule = {
      date: currentSchedule.date,
      time: currentSchedule.time,
      timezone: currentSchedule.timezone,
      type,
    };
    if (currentSchedule.type === "custom") {
      const rev = currentSchedule.repeatEvery;
      schedule.repeatEvery = typeof rev === "number" ? rev : (rev === "" ? 1 : parseInt(rev, 10) || 1);
      schedule.repeatUnit = (currentSchedule.repeatUnit || "day").toLowerCase();
      const repeatOn = Object.entries(currentSchedule.repeatDays || {})
        .filter(([, active]) => active)
        .map(([day]) => day);
      if (repeatOn.length > 0) schedule.repeatOn = repeatOn;
      if (currentSchedule.endOption === "on" && currentSchedule.endsOn) schedule.endsOn = currentSchedule.endsOn;
      if (currentSchedule.endOption === "after" && currentSchedule.endsAfter != null) schedule.endsAfter = currentSchedule.endsAfter;
    }
    const showRunDuring = currentSchedule.type === "hourly" ||
      (currentSchedule.type === "custom" && ["Minute", "Hour"].includes(currentSchedule.repeatUnit));
    if (showRunDuring) {
      const opt = currentSchedule.runDuringOption || "Financial Hour";
      schedule.startHour = opt === "Financial Hour" ? 8 : opt === "Non Financial Hour" ? 18 : 0;
      schedule.endHour = opt === "Financial Hour" ? 18 : opt === "Non Financial Hour" ? 23 : 23;
    }
    return schedule;
  };

  const handleAddSchedule = () => {
    if (!currentSchedule.date || !currentSchedule.time) {
      alert("Date and Time are required");
      return;
    }
    const schedule = buildScheduleFromCurrent();
    setSchedules((prev) => [...prev, schedule]);
    setCurrentSchedule({
      date: "",
      time: "",
      timezone: "Asia/Kolkata",
      type: "once",
      repeatEvery: 1,
      repeatUnit: "Hour",
      repeatDays: { Sunday: false, Monday: false, Tuesday: false, Wednesday: false, Thursday: false, Friday: false, Saturday: false },
      endOption: "after",
      endsOn: "",
      endsAfter: 10,
      runDuringOption: "Financial Hour",
    });
  };

  const handleScheduleRepeatEveryChange = (e) => {
    const raw = e.target.value;
    if (raw === "") {
      setCurrentSchedule((s) => ({ ...s, repeatEvery: "" }));
      return;
    }
    let val = parseInt(raw, 10);
    const max = SCHEDULE_MAX_REPEAT[currentSchedule.repeatUnit] || 1;
    if (isNaN(val)) val = 1;
    else if (val < 1) val = 1;
    else if (val > max) val = max;
    setCurrentSchedule((s) => ({ ...s, repeatEvery: val }));
  };

  const handleScheduleDayToggle = (dayName) => {
    setCurrentSchedule((s) => ({
      ...s,
      repeatDays: { ...(s.repeatDays || {}), [dayName]: !(s.repeatDays || {})[dayName] },
    }));
  };

  const headingStyle = {
    color: "#3730a3",
    fontSize: "28px",
    fontWeight: "bold",
    marginBottom: "24px",
    lineHeight: "1.2"
  };

  const closeButtonStyle = {
    height: "40px",
    padding: "0 16px",
    boxSizing: "border-box",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#3730a3",
    color: "#ffffff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
    marginBottom: "20px",
  };

  const inputStyle = {
    width: "100%",
    padding: "12px 14px",
    fontSize: "14px",
    backgroundColor: "#ffffff",
    border: "1px solid #3730a3",
    borderRadius: "6px",
    boxSizing: "border-box",
    color: "#1f2937",
    outline: "none"
  };

  const textareaStyle = {
    ...inputStyle,
    resize: "vertical",
    minHeight: "100px",
    fontFamily: "inherit"
  };

  const selectStyle = {
    ...inputStyle,
    cursor: "pointer"
  };

  const labelStyle = {
    display: "block",
    marginBottom: "8px",
    fontSize: "14px",
    fontWeight: "600",
    color: "#3730a3"
  };

  const buttonStyle = {
    padding: "12px 24px",
    fontSize: "14px",
    backgroundColor: "#3730a3",
    color: "#ffffff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
    transition: "background-color 0.2s"
  };

  const formGroupStyle = {
    marginBottom: "20px"
  };

  const successMessageStyle = {
    color: "#10b981",
    marginBottom: "16px",
    padding: "12px",
    backgroundColor: "#d1fae5",
    borderRadius: "6px",
    border: "1px solid #10b981"
  };

  const errorMessageStyle = {
    color: "#ef4444",
    marginBottom: "16px",
    padding: "12px",
    backgroundColor: "#fee2e2",
    borderRadius: "6px",
    border: "1px solid #ef4444"
  };

  return (
    <div>
      {onClose && (
        <button style={closeButtonStyle} onClick={onClose}>
          Close
        </button>
      )}
      <h2 style={headingStyle}>Create New Agent</h2>
      {successMessage && (
        <div style={successMessageStyle}>{successMessage}</div>
      )}
      {errorMessage && (
        <div style={errorMessageStyle}>{errorMessage}</div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={formGroupStyle}>
          <label style={labelStyle}>
            Agent Name
          </label>
          <input
            type="text"
            name="agent_name"
            value={agentData.agent_name}
            onChange={handleChange}
            required
            style={inputStyle}
          />
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>
            Agent Flow ID
          </label>
          <input
            type="text"
            name="agent_flow_id"
            value={agentData.agent_flow_id}
            onChange={handleChange}
            required
            style={inputStyle}
          />
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>
            Agent Category
          </label>
          <select
            value={categoryIsCustom ? "__new__" : (agentData.agent_category || "")}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "__new__") {
                setCategoryIsCustom(true);
                setAgentData((prev) => ({ ...prev, agent_category: "" }));
              } else {
                setCategoryIsCustom(false);
                setAgentData((prev) => ({ ...prev, agent_category: v }));
              }
            }}
            required={!categoryIsCustom}
            style={{ ...inputStyle, cursor: "pointer" }}
          >
            <option value="">Select category</option>
            {categories.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
            <option value="__new__">Add new</option>
          </select>
          {categoryIsCustom && (
            <input
              type="text"
              name="agent_category"
              value={agentData.agent_category}
              onChange={handleChange}
              placeholder="Enter new category"
              required
              style={{ ...inputStyle, marginTop: "8px" }}
            />
          )}
        </div>

        <IndustryContextPicker
          value={industryContext}
          onChange={setIndustryContext}
          inputStyle={inputStyle}
          selectStyle={selectStyle}
          labelStyle={labelStyle}
          formGroupStyle={formGroupStyle}
        />

        <div style={formGroupStyle}>
          <label style={labelStyle}>
            Description
          </label>
          <textarea
            name="description"
            value={agentData.description}
            onChange={handleChange}
            rows="4"
            required
            style={textareaStyle}
          />
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>
            Instructions
          </label>
          <p style={{ fontSize: "13px", color: "#6b7280", marginBottom: "10px" }}>
            Add one instruction per step. Drag the handle to reorder. They will be sent in order, joined by &quot; || &quot;.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {instructionSteps.map((step, index) => (
              <div
                key={step.id}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  setDragOverStepIndex(index);
                }}
                onDragLeave={() => setDragOverStepIndex(null)}
                onDrop={(e) => {
                  e.preventDefault();
                  const fromIndex = parseInt(e.dataTransfer.getData("text/plain"), 10);
                  if (Number.isNaN(fromIndex)) return;
                  reorderInstructionSteps(fromIndex, index);
                  setDraggedStepIndex(null);
                  setDragOverStepIndex(null);
                }}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "10px",
                  border: "1px solid #e5e7eb",
                  borderRadius: "6px",
                  padding: "10px 12px",
                  backgroundColor: dragOverStepIndex === index ? "#eef2ff" : "#fafafa",
                  opacity: draggedStepIndex === index ? 0.6 : 1,
                  transition: "background-color 0.15s ease",
                }}
              >
                <div
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData("text/plain", String(index));
                    e.dataTransfer.effectAllowed = "move";
                    setDraggedStepIndex(index);
                  }}
                  onDragEnd={() => setDraggedStepIndex(null)}
                  style={{
                    flexShrink: 0,
                    cursor: draggedStepIndex === index ? "grabbing" : "grab",
                    color: "#6b7280",
                    padding: "4px",
                    marginTop: "4px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  title="Drag to reorder"
                >
                  <RiDragMove2Line size={18} />
                </div>
                <span
                  style={{
                    flexShrink: 0,
                    width: "24px",
                    height: "24px",
                    borderRadius: "50%",
                    backgroundColor: "#3730a3",
                    color: "#fff",
                    fontSize: "12px",
                    fontWeight: "600",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginTop: "6px",
                  }}
                >
                  {index + 1}
                </span>
                <AutoResizeTextarea
                  value={step.content}
                  onChange={(e) => {
                    setInstructionSteps((prev) =>
                      prev.map((s) =>
                        s.id === step.id ? { ...s, content: e.target.value } : s
                      )
                    );
                  }}
                  placeholder="Enter step instruction..."
                  rows={2}
                  style={{
                    ...textareaStyle,
                    flex: 1,
                    minHeight: "56px",
                    margin: 0,
                    backgroundColor: "#fff",
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    if (instructionSteps.length > 1) {
                      setInstructionSteps((prev) => prev.filter((s) => s.id !== step.id));
                    }
                  }}
                  disabled={instructionSteps.length <= 1}
                  title={instructionSteps.length <= 1 ? "Keep at least one step" : "Remove step"}
                  style={{
                    flexShrink: 0,
                    padding: "6px 10px",
                    border: "none",
                    borderRadius: "6px",
                    backgroundColor: "#fef2f2",
                    color: "#b91c1c",
                    cursor: instructionSteps.length <= 1 ? "not-allowed" : "pointer",
                    fontSize: "12px",
                    fontWeight: "600",
                    opacity: instructionSteps.length <= 1 ? 0.6 : 1,
                    marginTop: "4px",
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setInstructionSteps((prev) => [...prev, { id: stepId(), content: "" }])}
            style={{
              marginTop: "10px",
              padding: "8px 14px",
              fontSize: "13px",
              fontWeight: "600",
              color: "#3730a3",
              backgroundColor: "#eef2ff",
              border: "1px solid #3730a3",
              borderRadius: "6px",
              cursor: "pointer",
            }}
          >
            + Add Step
          </button>
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>
            Organisation Visibility
          </label>
          <div style={{ maxWidth: 480 }}>
            <MultiSelectDropdown
              options={buildOrgMultiSelectOptions(availableOrgs, selectedOrgIds)}
              value={selectedOrgIds.map((id) => String(id))}
              onChange={(vals) => setSelectedOrgIds(parseOrgIdsFromMultiSelectValues(vals))}
              placeholder="Nothing Selected means Shown to All"
              searchable
              optionColumns={2}
              aria-label="Organisation visibility"
            />
          </div>
          <p style={{ margin: "8px 0 0 0", fontSize: "12px", color: "#6b7280" }}>
            If nothing is selected, it will be shown to all.
          </p>
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>
            Lookback Period
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            <span style={{ fontSize: "14px", color: "#1f2937", fontWeight: "500" }}>Last</span>
            <input
              type="number"
              value={lookbackPeriod}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "") {
                  setLookbackPeriod("");
                  return;
                }
                if (/^\d+$/.test(value)) setLookbackPeriod(value);
              }}
              placeholder="1"
              min={1}
              step={1}
              style={{
                ...inputStyle,
                width: "72px",
                textAlign: "center",
                padding: "10px 12px",
                border: (() => {
                  const v = (lookbackPeriod || "").toString().trim();
                  const isInvalid = v === "" || v === "0";
                  return isInvalid ? "1px solid #ef4444" : "1px solid #3730a3";
                })(),
              }}
            />
            <div style={{ position: "relative", display: "inline-block" }}>
              <select
                value={lookbackUnit}
                onChange={(e) => {
                  const newUnit = e.target.value;
                  let newValue = lookbackPeriod;
                  if (!newValue || newValue === "0" || parseInt(newValue, 10) < 1) {
                    newValue = "1";
                  }
                  setLookbackUnit(newUnit);
                  if (newValue !== lookbackPeriod) setLookbackPeriod(newValue);
                }}
                style={{
                  ...selectStyle,
                  width: "160px",
                  paddingRight: "32px",
                  appearance: "none",
                }}
              >
                <option value="Day">Day</option>
                <option value="Month">Month</option>
                <option value="Quarter">Quarter</option>
                <option value="Year">Year</option>
                <option value="Financial Year">Financial Year</option>
              </select>
              <span style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "#6b7280", fontSize: "12px" }}>▼</span>
            </div>
          </div>
          {((lookbackPeriod || "").toString().trim() === "" || (lookbackPeriod || "").toString().trim() === "0") && (
            <div style={{ marginTop: "6px", fontSize: "12px", color: "#dc2626" }}>Lookback Period cannot be blank or 0</div>
          )}
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>
            Writing Style
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <select
              value={writingStyle}
              onChange={(e) => setWritingStyle(e.target.value)}
              style={{ ...selectStyle, width: "220px" }}
            >
              <option value="">— Select —</option>
              <option value="detailed_report">Detailed Report</option>
              <option value="short_report">Short Report</option>
              <option value="tabular">Tabular</option>
              <option value="bullet_points">Bullet Points</option>
              <option value="simplify_real_scenarios">Simplify Real Scenarios</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          {writingStyle === "custom" && (
            <div style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "10px", maxWidth: "500px" }}>
              <div>
                <label style={{ display: "block", marginBottom: "4px", fontSize: "13px", fontWeight: "500", color: "#4b5563" }}>Custom style ID (optional)</label>
                <input
                  type="text"
                  value={customWritingStyleId}
                  onChange={(e) => setCustomWritingStyleId(e.target.value)}
                  placeholder="e.g. custom style id"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={{ display: "block", marginBottom: "4px", fontSize: "13px", fontWeight: "500", color: "#4b5563" }}>Custom style name</label>
                <input
                  type="text"
                  value={customWritingStyleName}
                  onChange={(e) => setCustomWritingStyleName(e.target.value)}
                  placeholder="Custom writing style name"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={{ display: "block", marginBottom: "4px", fontSize: "13px", fontWeight: "500", color: "#4b5563" }}>Custom style description</label>
                <textarea
                  value={customWritingStyleDescription}
                  onChange={(e) => setCustomWritingStyleDescription(e.target.value)}
                  placeholder="Describe the custom writing style"
                  rows={3}
                  style={{ ...inputStyle, resize: "vertical", minHeight: "80px" }}
                />
              </div>
            </div>
          )}
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>
            Web Links
          </label>
          <div style={{ display: "flex", marginBottom: "10px" }}>
            <input
              type="text"
              value={currentLink}
              onChange={(e) => setCurrentLink(e.target.value)}
              style={{
                ...inputStyle,
                width: "calc(100% - 120px)",
                marginRight: "10px",
              }}
            />
            <button
              type="button"
              onClick={() => {
                if (currentLink) {
                  setWebLinks([...webLinks, currentLink]);
                  setCurrentLink("");
                }
              }}
              style={buttonStyle}
            >
              Add Link
            </button>
          </div>
          <div>
            {webLinks.map((link, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "5px",
                }}
              >
                <span>{link}</span>
                <button
                  type="button"
                  onClick={() => {
                    setWebLinks(webLinks.filter((_, i) => i !== index));
                  }}
                  style={{
                    padding: "6px 12px",
                    backgroundColor: "#ef4444",
                    color: "#ffffff",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: "600"
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>
            Web Search
          </label>
          <div style={{ display: "inline-flex", borderRadius: "8px", border: "1px solid #d1d5db", overflow: "hidden" }}>
            <button
              type="button"
              onClick={() => setWebSearch("Off")}
              style={{
                padding: "10px 16px",
                fontSize: "14px",
                fontWeight: "500",
                border: "none",
                cursor: "pointer",
                transition: "background-color 0.2s, color 0.2s",
                ...(webSearch === "Off"
                  ? { backgroundColor: "#3730a3", color: "#ffffff" }
                  : { backgroundColor: "#ffffff", color: "#1f2937" }
                ),
              }}
            >
              OFF
            </button>
            <button
              type="button"
              onClick={() => setWebSearch("On")}
              style={{
                padding: "10px 16px",
                fontSize: "14px",
                fontWeight: "500",
                border: "none",
                cursor: "pointer",
                transition: "background-color 0.2s, color 0.2s",
                ...(webSearch === "On"
                  ? { backgroundColor: "#3730a3", color: "#ffffff" }
                  : { backgroundColor: "#ffffff", color: "#1f2937" }
                ),
              }}
            >
              ON
            </button>
          </div>
        </div>
        <div style={formGroupStyle}>
          <label style={labelStyle}>
            Auto Data Selection
          </label>
          <div style={{ display: "inline-flex", borderRadius: "8px", border: "1px solid #d1d5db", overflow: "hidden" }}>
            <button
              type="button"
              onClick={() => setAutoDataSelection(false)}
              style={{
                padding: "10px 16px",
                fontSize: "14px",
                fontWeight: "500",
                border: "none",
                cursor: "pointer",
                transition: "background-color 0.2s, color 0.2s",
                ...(!autoDataSelection
                  ? { backgroundColor: "#3730a3", color: "#ffffff" }
                  : { backgroundColor: "#ffffff", color: "#1f2937" }
                ),
              }}
            >
              OFF
            </button>
            <button
              type="button"
              onClick={() => setAutoDataSelection(true)}
              style={{
                padding: "10px 16px",
                fontSize: "14px",
                fontWeight: "500",
                border: "none",
                cursor: "pointer",
                transition: "background-color 0.2s, color 0.2s",
                ...(autoDataSelection
                  ? { backgroundColor: "#3730a3", color: "#ffffff" }
                  : { backgroundColor: "#ffffff", color: "#1f2937" }
                ),
              }}
            >
              ON
            </button>
          </div>
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>
            Select Form Types
          </label>
          
          <div style={{ display: "flex", gap: "20px" }}>
            {/* India Forms */}
            <div style={{ flex: 1 }}>
              <h4 style={{ color: "#3730a3", fontSize: "16px", fontWeight: "600", marginBottom: "12px" }}>India</h4>
              <input
                type="text"
                placeholder="Search India forms..."
                value={formSearch.India}
                onChange={(e) => setFormSearch({ ...formSearch, India: e.target.value })}
                style={{ ...inputStyle, padding: "10px 12px", marginBottom: "10px", fontSize: "13px" }}
              />
              <div style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid #3730a3", borderRadius: "6px", padding: "12px", backgroundColor: "#f9fafb" }}>
                {Object.entries(availableForms.India || {})
                  .filter(([key, description]) => 
                    key.toLowerCase().includes(formSearch.India.toLowerCase()) || 
                    description.toLowerCase().includes(formSearch.India.toLowerCase())
                  )
                  .map(([key, description]) => (
                  <div key={key} style={{ marginBottom: "5px" }}>
                    <label style={{ display: "flex", alignItems: "start", gap: "5px" }}>
                      <input
                        type="checkbox"
                        checked={(selectedForms.India || []).includes(key)}
                        onChange={(e) => handleFormSelection("India", key, e.target.checked)}
                      />
                      <span>
                        <strong>{key}</strong>: {description}
                      </span>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* USA Forms */}
            <div style={{ flex: 1 }}>
              <h4 style={{ color: "#3730a3", fontSize: "16px", fontWeight: "600", marginBottom: "12px" }}>USA</h4>
              <input
                type="text"
                placeholder="Search USA forms..."
                value={formSearch.USA}
                onChange={(e) => setFormSearch({ ...formSearch, USA: e.target.value })}
                style={{ ...inputStyle, padding: "10px 12px", marginBottom: "10px", fontSize: "13px" }}
              />
              <div style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid #3730a3", borderRadius: "6px", padding: "12px", backgroundColor: "#f9fafb" }}>
                {Object.entries(availableForms.USA || {})
                  .filter(([key, description]) => 
                    key.toLowerCase().includes(formSearch.USA.toLowerCase()) || 
                    description.toLowerCase().includes(formSearch.USA.toLowerCase())
                  )
                  .map(([key, description]) => (
                  <div key={key} style={{ marginBottom: "5px" }}>
                    <label style={{ display: "flex", alignItems: "start", gap: "5px" }}>
                      <input
                        type="checkbox"
                        checked={(selectedForms.USA || []).includes(key)}
                        onChange={(e) => handleFormSelection("USA", key, e.target.checked)}
                      />
                      <span>
                        <strong>{key}</strong>: {description}
                      </span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>
            Agent Scheduling
          </label>
          <div style={{ border: "1px solid #3730a3", padding: "20px", borderRadius: "6px", backgroundColor: "#f9fafb" }}>
            <div style={{ display: "flex", gap: "16px", marginBottom: "12px", flexWrap: "wrap" }}>
              <div style={{ flex: "1", minWidth: "120px" }}>
                <label style={{ display: "block", fontSize: "13px", marginBottom: "4px", fontWeight: "600", color: "#3730a3" }}>Date</label>
                <input
                  type="date"
                  value={currentSchedule.date}
                  onChange={(e) => setCurrentSchedule((s) => ({ ...s, date: e.target.value }))}
                  style={{ ...inputStyle, padding: "10px 12px" }}
                />
              </div>
              <div style={{ flex: "1", minWidth: "120px" }}>
                <label style={{ display: "block", fontSize: "13px", marginBottom: "4px", fontWeight: "600", color: "#3730a3" }}>Time</label>
                <input
                  type="time"
                  value={currentSchedule.time}
                  onChange={(e) => setCurrentSchedule((s) => ({ ...s, time: e.target.value }))}
                  style={{ ...inputStyle, padding: "10px 12px" }}
                />
              </div>
            </div>
            <div style={{ marginBottom: "12px" }}>
              <label style={{ display: "block", fontSize: "13px", marginBottom: "4px", fontWeight: "600", color: "#3730a3" }}>Timezone</label>
              <select
                value={currentSchedule.timezone}
                onChange={(e) => setCurrentSchedule((s) => ({ ...s, timezone: e.target.value }))}
                style={{ ...selectStyle, padding: "10px 12px", maxWidth: "320px" }}
              >
                {SCHEDULE_TIMEZONES.map((tz) => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: "12px" }}>
              <label style={{ display: "block", fontSize: "13px", marginBottom: "4px", fontWeight: "600", color: "#3730a3" }}>Repeat</label>
              <select
                value={currentSchedule.type}
                onChange={(e) => setCurrentSchedule((s) => ({ ...s, type: e.target.value }))}
                style={{ ...selectStyle, padding: "10px 12px", maxWidth: "320px" }}
              >
                <option value="once">Don&apos;t repeat</option>
                <option value="hourly">Every hour</option>
                <option value="daily">Daily</option>
                <option value="weekly">Every Week</option>
                <option value="monthly">Every Month</option>
                <option value="quarterly">Every Quarter</option>
                <option value="weekday">Every Weekday (Mon-Fri)</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            {currentSchedule.type === "custom" && (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                  <label style={{ fontSize: "13px", fontWeight: "500", color: "#374151", width: "90px" }}>Repeat every:</label>
                  <input
                    type="number"
                    min={1}
                    value={currentSchedule.repeatEvery === "" ? "" : currentSchedule.repeatEvery}
                    onChange={handleScheduleRepeatEveryChange}
                    style={{ ...inputStyle, width: "64px", padding: "8px", textAlign: "center" }}
                  />
                  <select
                    value={currentSchedule.repeatUnit}
                    onChange={(e) => setCurrentSchedule((s) => ({ ...s, repeatUnit: e.target.value }))}
                    style={{ ...selectStyle, width: "100px", padding: "8px" }}
                  >
                    <option value="Minute">Minute</option>
                    <option value="Hour">Hour</option>
                    <option value="Day">Day</option>
                    <option value="Week">Week</option>
                    <option value="Month">Month</option>
                  </select>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                  <label style={{ fontSize: "13px", fontWeight: "500", color: "#374151", width: "90px" }}>Repeat on:</label>
                  <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                    {SCHEDULE_DAY_MAP.map(({ label, name }) => (
                      <button
                        key={name}
                        type="button"
                        onClick={() => handleScheduleDayToggle(name)}
                        style={{
                          width: "36px",
                          height: "36px",
                          borderRadius: "50%",
                          border: "2px solid #3730a3",
                          background: (currentSchedule.repeatDays || {})[name] ? "#3730a3" : "transparent",
                          color: (currentSchedule.repeatDays || {})[name] ? "#fff" : "#3730a3",
                          fontWeight: "600",
                          fontSize: "12px",
                          cursor: "pointer",
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ marginBottom: "12px" }}>
                  <label style={{ display: "block", fontSize: "13px", marginBottom: "6px", fontWeight: "500", color: "#374151" }}>Ends:</label>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                      <input type="radio" name="endOption" checked={currentSchedule.endOption === "never"} onChange={() => setCurrentSchedule((s) => ({ ...s, endOption: "never" }))} />
                      <span style={{ fontSize: "14px" }}>Never</span>
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                      <input type="radio" name="endOption" checked={currentSchedule.endOption === "on"} onChange={() => setCurrentSchedule((s) => ({ ...s, endOption: "on" }))} />
                      <span style={{ fontSize: "14px" }}>On</span>
                      <input type="date" value={currentSchedule.endsOn || ""} onChange={(e) => setCurrentSchedule((s) => ({ ...s, endsOn: e.target.value }))} disabled={currentSchedule.endOption !== "on"} style={{ ...inputStyle, marginLeft: "8px", width: "140px", padding: "6px" }} />
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                      <input type="radio" name="endOption" checked={currentSchedule.endOption === "after"} onChange={() => setCurrentSchedule((s) => ({ ...s, endOption: "after" }))} />
                      <span style={{ fontSize: "14px" }}>After</span>
                      <input type="number" min={1} value={currentSchedule.endsAfter ?? 10} onChange={(e) => setCurrentSchedule((s) => ({ ...s, endsAfter: parseInt(e.target.value, 10) || 10 }))} disabled={currentSchedule.endOption !== "after"} style={{ ...inputStyle, width: "60px", marginLeft: "8px", padding: "6px", textAlign: "center" }} />
                      <span style={{ fontSize: "14px", marginLeft: "4px" }}>runs</span>
                    </label>
                  </div>
                </div>
              </>
            )}
            {(currentSchedule.type === "hourly" || (currentSchedule.type === "custom" && ["Minute", "Hour"].includes(currentSchedule.repeatUnit))) && (
              <div style={{ marginBottom: "12px" }}>
                <label style={{ display: "block", fontSize: "13px", marginBottom: "4px", fontWeight: "500", color: "#374151" }}>Run during:</label>
                <select
                  value={currentSchedule.runDuringOption || "Financial Hour"}
                  onChange={(e) => setCurrentSchedule((s) => ({ ...s, runDuringOption: e.target.value }))}
                  style={{ ...selectStyle, padding: "8px", maxWidth: "280px" }}
                >
                  <option value="Financial Hour">Financial Hour (8am - 6pm)</option>
                  <option value="Non Financial Hour">Non Financial Hour</option>
                  <option value="Both">Both</option>
                </select>
              </div>
            )}
            <button type="button" onClick={handleAddSchedule} style={{ ...buttonStyle, marginBottom: "10px" }}>
              Add Schedule
            </button>
            {schedules.length > 0 && (
              <div style={{ marginTop: "12px", borderTop: "1px solid #e5e7eb", paddingTop: "12px" }}>
                <div style={{ fontSize: "13px", fontWeight: "600", marginBottom: "8px", color: "#3730a3" }}>Schedules added (stored in metadata):</div>
                {schedules.map((sch, index) => (
                  <div key={index} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px", backgroundColor: "#fff", padding: "10px 12px", borderRadius: "6px", border: "1px solid #e5e7eb" }}>
                    <span style={{ fontSize: "14px" }}>
                      {sch.date} {sch.time} ({sch.timezone}) — {sch.type}
                      {sch.repeatEvery != null && sch.repeatUnit && ` · every ${sch.repeatEvery} ${sch.repeatUnit}`}
                      {sch.repeatOn && sch.repeatOn.length > 0 && ` · on ${sch.repeatOn.join(", ")}`}
                      {sch.endsOn && ` · ends ${sch.endsOn}`}
                      {sch.endsAfter != null && sch.endsAfter > 0 && ` · after ${sch.endsAfter} runs`}
                    </span>
                    <button type="button" onClick={() => setSchedules((prev) => prev.filter((_, i) => i !== index))} style={{ padding: "6px 12px", backgroundColor: "#ef4444", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "12px", fontWeight: "600" }}>
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          style={{
            ...buttonStyle,
            opacity: loading ? 0.6 : 1,
            cursor: loading ? "not-allowed" : "pointer"
          }}
        >
          {loading ? "Creating..." : "Create Agent"}
        </button>
      </form>
    </div>
  );
};

export default CreateAgent;
